/**
 * F-148 §10.2 / §4.8 (AC10, AC11): 2×2 labelled quantity grid. `inputMode="numeric"`,
 * `aria-invalid` and `aria-describedby` per field. Edits go to the parent as integers or null.
 * Non-integer text (signs, decimals, letters) is reported as the `INVALID_QUANTITY` sentinel so
 * the shared validator flags QUANTITY_OUT_OF_RANGE while the typed text stays visible.
 * Typing accepted (even the calculated value) makes the line's `acceptedAuto` false — that
 * decision lives in the shared `applyQuantityEdit`, not here.
 */
import { useEffect, useId, useState } from 'react';
import { useTranslation } from '@dukanti/shared';
import { RECEIVING_LIMITS, type ReceivingLineIssue } from '@dukanti/shared/admin';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { issueMessageKey } from '../lib/error-messages';

export type QuantityGridField = 'expectedUnits' | 'acceptedUnits' | 'damagedUnits' | 'missingUnits';

/** Sentinel for unparsable text; always out of range so validation blocks it. */
export const INVALID_QUANTITY = -1;

// eslint-disable-next-line react-refresh/only-export-components -- input parsing contract shared with tests
export function parseQuantityText(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^[0-9]+$/u.test(trimmed)) return INVALID_QUANTITY;
  return Number.parseInt(trimmed, 10);
}

export interface QuantityInputProps {
  readonly id: string;
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  readonly invalid?: boolean;
  readonly describedBy?: string;
  readonly readOnly?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly testId?: string;
  readonly acceptedAuto?: boolean;
}

/**
 * Controlled integer input that keeps the merchant's typed text while it is invalid
 * (`value === INVALID_QUANTITY`) and otherwise mirrors the numeric value from props.
 */
export function QuantityInput({
  id,
  value,
  onChange,
  invalid = false,
  describedBy,
  readOnly = false,
  disabled = false,
  className,
  testId,
  acceptedAuto,
}: QuantityInputProps): React.JSX.Element {
  const [text, setText] = useState(value === null || value < 0 ? '' : String(value));

  useEffect(() => {
    if (value === INVALID_QUANTITY) return;
    if (parseQuantityText(text) !== value) setText(value === null ? '' : String(value));
    // Only prop changes should resync; `text` is intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      id={id}
      type="text"
      inputMode="numeric"
      dir="ltr"
      autoComplete="off"
      value={text}
      readOnly={readOnly}
      disabled={disabled}
      onChange={(event) => {
        setText(event.target.value);
        onChange(parseQuantityText(event.target.value));
      }}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={describedBy}
      className={cn(
        'min-h-11 text-start tabular-nums',
        readOnly ? 'bg-admin-surface-muted' : '',
        className,
      )}
      data-testid={testId}
      data-accepted-auto={acceptedAuto === undefined ? undefined : String(acceptedAuto)}
    />
  );
}

export interface LineQuantityGridProps {
  readonly expectedUnits: number | null;
  readonly acceptedUnits: number | null;
  readonly damagedUnits: number | null;
  readonly missingUnits: number | null;
  readonly acceptedAuto: boolean;
  /** Pack lines derive expected from packs × units-per-pack; the field is then read-only. */
  readonly expectedReadOnly?: boolean;
  readonly issues: readonly ReceivingLineIssue[];
  readonly disabled?: boolean;
  readonly onEdit: (field: QuantityGridField, value: number | null) => void;
  readonly className?: string;
}

const FIELDS: readonly QuantityGridField[] = [
  'expectedUnits',
  'acceptedUnits',
  'damagedUnits',
  'missingUnits',
];

const LABEL_KEYS = {
  expectedUnits: 'admin.receiving.line.expected',
  acceptedUnits: 'admin.receiving.line.accepted',
  damagedUnits: 'admin.receiving.line.damaged',
  missingUnits: 'admin.receiving.line.missing',
} as const;

export function LineQuantityGrid({
  expectedUnits,
  acceptedUnits,
  damagedUnits,
  missingUnits,
  acceptedAuto,
  expectedReadOnly = false,
  issues,
  disabled = false,
  onEdit,
  className,
}: LineQuantityGridProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const values: Record<QuantityGridField, number | null> = {
    expectedUnits,
    acceptedUnits,
    damagedUnits,
    missingUnits,
  };

  return (
    <div
      className={cn('grid grid-cols-2 gap-3', className)}
      role="group"
      aria-label={t('admin.receiving.line.quantities')}
      data-testid="line-quantity-grid"
    >
      {FIELDS.map((field) => {
        const fieldId = `${id}-${field}`;
        const first = issues.find((issue) => issue.field === field);
        const readOnly = field === 'expectedUnits' && expectedReadOnly;
        return (
          <div key={field} className="space-y-1 text-start">
            <Label htmlFor={fieldId} className="text-xs">
              {t(LABEL_KEYS[field])}
            </Label>
            <QuantityInput
              id={fieldId}
              value={values[field]}
              onChange={(value) => onEdit(field, value)}
              readOnly={readOnly}
              disabled={disabled}
              invalid={Boolean(first)}
              describedBy={first ? `${fieldId}-error` : undefined}
              className={
                field === 'acceptedUnits' && !acceptedAuto ? 'border-admin-lime' : undefined
              }
              testId={`quantity-${field}`}
              acceptedAuto={field === 'acceptedUnits' ? acceptedAuto : undefined}
            />
            {first ? (
              <p
                id={`${fieldId}-error`}
                className="text-destructive text-xs"
                data-testid={`quantity-${field}-error`}
              >
                {t(issueMessageKey(first.code), {
                  ...first.meta,
                  max: RECEIVING_LIMITS.maxQuantity,
                })}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
